import { useEffect, useState } from "react";
import { Input, Modal, MultiSelect } from "../../../BP2Components";
import IntlMessages from "../../../../util/IntlMessages";
import "./FormMethods.less";

interface Requirement extends Record<string, string> {
   name: string;
   field: string;
}

interface Method {
   label: string;
   name: string;
   parameters: Record<string, string>;
   arguments: Record<string, string>;
   requirements: Requirement[];
}

interface SelectOption {
   value: string;
   label: string;
}

interface FieldDef {
   name: string;
   placeholder: string;
}

interface MethodModalProps {
   isOpen: boolean;
   onOk: (method: Method) => void;
   onCancel: () => void;
   initialMethod: Method | null;
   availableActions: SelectOption[];
}

const EMPTY_METHOD: Method = {
   label: "",
   name: "",
   parameters: {},
   arguments: {},
   requirements: [],
};

const INITIAL_ERRORS = { name: false, arguments: false, parameters: false };

const toEntries = (obj: Record<string, string> | undefined): Record<string, string>[] =>
   Object.entries(obj ?? {}).map(([key, value]) => ({ key, value }));

const fromEntries = (entries: Record<string, string>[]): Record<string, string> =>
   Object.fromEntries(
      entries.map((entry) => [entry.key.trim(), entry.value]).filter(([key]) => key),
   );

const hasDuplicateKeys = (entries: Record<string, string>[]): boolean => {
   const keys = entries.map((entry) => entry.key.trim()).filter(Boolean);
   return keys.length !== new Set(keys).size;
};

interface RowEditorProps {
   rows: Record<string, string>[];
   onChange: (rows: Record<string, string>[]) => void;
   fields: FieldDef[];
}

const RowEditor = ({ rows, onChange, fields }: RowEditorProps) => {
   const emptyRow = Object.fromEntries(fields.map((field) => [field.name, ""]));
   const addRow = () => onChange([...rows, emptyRow]);
   const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));
   const updateField = (index: number, fieldName: string, value: string) =>
      onChange(rows.map((row, i) => (i === index ? { ...row, [fieldName]: value } : row)));

   return (
      <div className="form-methods-kv-editor">
         {rows.map((row, index) => (
            <div key={index} className="form-methods-kv-row">
               {fields.map((field) => (
                  <Input
                     key={field.name}
                     placeholder={field.placeholder}
                     value={row[field.name]}
                     onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateField(index, field.name, e.target.value)
                     }
                  />
               ))}
               <button
                  type="button"
                  className="form-methods-row-remove"
                  aria-label={`Usuń wiersz ${index + 1}`}
                  onClick={() => removeRow(index)}
               >
                  ×
               </button>
            </div>
         ))}
         <button type="button" className="form-methods-row-add" onClick={addRow}>
            + <IntlMessages id="form.methods.addRow" defaultMessage="Dodaj" />
         </button>
      </div>
   );
};

const ARGUMENT_FIELDS: FieldDef[] = [
   { name: "key", placeholder: "klucz" },
   { name: "value", placeholder: "wartość lub {hId}" },
];

const PARAMETER_FIELDS: FieldDef[] = [
   { name: "key", placeholder: "nazwa parametru" },
   { name: "value", placeholder: "wartość domyślna" },
];

const REQUIREMENT_FIELDS: FieldDef[] = [
   { name: "name", placeholder: "nazwa wymagalności" },
   { name: "field", placeholder: "hId pola formularza" },
];

export const MethodModal = ({
   isOpen,
   onOk,
   onCancel,
   initialMethod,
   availableActions,
}: MethodModalProps) => {
   const [editing, setEditing] = useState<{
      label: string;
      name: string;
      argumentEntries: Record<string, string>[];
      parameterEntries: Record<string, string>[];
      requirements: Requirement[];
   }>({
      label: "",
      name: "",
      argumentEntries: [],
      parameterEntries: [],
      requirements: [],
   });
   const [errors, setErrors] = useState(INITIAL_ERRORS);

   useEffect(() => {
      if (!isOpen) return;
      const source = initialMethod ?? EMPTY_METHOD;
      setEditing({
         label: source.label,
         name: source.name,
         argumentEntries: toEntries(source.arguments),
         parameterEntries: toEntries(source.parameters),
         requirements: source.requirements ?? [],
      });
      setErrors(INITIAL_ERRORS);
   }, [isOpen, initialMethod]);

   const update =
      <K extends keyof typeof editing>(field: K) =>
      (value: (typeof editing)[K]) =>
         setEditing((prev) => ({ ...prev, [field]: value }));

   const handleOk = () => {
      const nameInvalid = !editing.name.trim();
      const argumentsDuplicate = hasDuplicateKeys(editing.argumentEntries);
      const parametersDuplicate = hasDuplicateKeys(editing.parameterEntries);

      setErrors({
         name: nameInvalid,
         arguments: argumentsDuplicate,
         parameters: parametersDuplicate,
      });

      if (nameInvalid || argumentsDuplicate || parametersDuplicate) return;

      onOk({
         label: editing.label,
         name: editing.name,
         arguments: fromEntries(editing.argumentEntries),
         parameters: fromEntries(editing.parameterEntries),
         requirements: editing.requirements,
      });
   };

   return (
      <Modal isOpen={isOpen} onOk={handleOk} onCancel={onCancel} height={620}>
         <div className="form-methods-modal">
            <div className="form-methods-modal-field">
               <label>
                  <IntlMessages id="form.methods.label" defaultMessage="Etykieta" />
               </label>
               <Input
                  value={editing.label}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                     update("label")(e.target.value)
                  }
               />
            </div>

            <div className="form-methods-modal-field">
               <label>
                  <IntlMessages id="form.methods.name" defaultMessage="Metoda biznesowa" />
               </label>
               <MultiSelect
                  name=""
                  placeholder=""
                  noOptionsMessage=""
                  size="medium"
                  hideArrow={false}
                  options={availableActions}
                  isMulti={false}
                  closeMenuOnSelect
                  value={availableActions.find((a) => a.value === editing.name) ?? null}
                  onChange={(option: SelectOption | null) => {
                     update("name")(option?.value ?? "");
                     setErrors((prev) => ({ ...prev, name: false }));
                  }}
                  isCreatable={false}
               />
               {errors.name && (
                  <span className="form-methods-error">
                     <IntlMessages
                        id="form.methods.nameRequired"
                        defaultMessage="Wybierz metodę biznesową"
                     />
                  </span>
               )}
            </div>

            <div className="form-methods-modal-field">
               <label>
                  <IntlMessages id="form.methods.arguments" defaultMessage="Argumenty" />
               </label>
               <p className="form-methods-hint">
                  <IntlMessages
                     id="form.methods.argumentsHint"
                     defaultMessage="Wartość stała lub referencja do pola formularza w formacie {hId}"
                  />
               </p>
               <RowEditor
                  rows={editing.argumentEntries}
                  onChange={(value) => {
                     update("argumentEntries")(value);
                     setErrors((prev) => ({ ...prev, arguments: false }));
                  }}
                  fields={ARGUMENT_FIELDS}
               />
               {errors.arguments && (
                  <span className="form-methods-error">
                     <IntlMessages
                        id="form.methods.duplicateKeys"
                        defaultMessage="Klucze muszą być unikalne"
                     />
                  </span>
               )}
            </div>

            <div className="form-methods-modal-field">
               <label>
                  <IntlMessages id="form.methods.parameters" defaultMessage="Parametry runtime" />
               </label>
               <p className="form-methods-hint">
                  <IntlMessages
                     id="form.methods.parametersHint"
                     defaultMessage="Wartości, o które użytkownik zostanie zapytany tuż przed wykonaniem metody"
                  />
               </p>
               <RowEditor
                  rows={editing.parameterEntries}
                  onChange={(value) => {
                     update("parameterEntries")(value);
                     setErrors((prev) => ({ ...prev, parameters: false }));
                  }}
                  fields={PARAMETER_FIELDS}
               />
               {errors.parameters && (
                  <span className="form-methods-error">
                     <IntlMessages
                        id="form.methods.duplicateKeys"
                        defaultMessage="Klucze muszą być unikalne"
                     />
                  </span>
               )}
            </div>

            <div className="form-methods-modal-field">
               <label>
                  <IntlMessages id="form.methods.requirements" defaultMessage="Wymagalności" />
               </label>
               <RowEditor
                  rows={editing.requirements}
                  onChange={(value) =>
                     update("requirements")(
                        value.map((row) => ({ name: row.name, field: row.field })),
                     )
                  }
                  fields={REQUIREMENT_FIELDS}
               />
            </div>
         </div>
      </Modal>
   );
};
